import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { VbPaymentChangePaymentStepFormGroup } from '../../../../../models/shared/forms/vb-payment-change-payment-step-form-group';
import { Subscribable } from '../../../../../models/base/subscribable';
import { startWith } from 'rxjs/operators';
import { DropDownItem } from '../../../../../models/shared/stylesheet/drop-down-item';
import * as moment from 'moment';
import { BaseButtonSizeEnum } from '../../base-button/base-button-size.enum';
import { DateUtils } from '../../../../../utils/date-utils';

@Component({
  selector: 'app-vb-payment-change-lump-sum-payment-card',
  templateUrl: './vb-payment-change-lump-sum-payment-card.component.html',
  styleUrls: ['./vb-payment-change-lump-sum-payment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class VbPaymentChangeLumpSumPaymentCardComponent extends Subscribable implements OnChanges {
  @Input() paymentStepForm!: VbPaymentChangePaymentStepFormGroup;
  @Input() formSubmitted: boolean = false;
  @Input() allowableChangeDate: string = '';
  @Output() lumpSumRemoved = new EventEmitter<void>();

  private _displayLumpSumForm = new BehaviorSubject<boolean>(false);
  public displayLumpSumForm$ = this._displayLumpSumForm.asObservable();

  public customErrorMessages = {
    lumpSumPaymentMonth: {
      required: $localize`Payment Start Month is required`,
      paymentStartBeforeAllowableChangeDate: $localize`Date cannot be before the allowable change date`
    },
    lumpSumPaymentYear: {
      required: $localize`Payment Year is required`
    },
    lumpSumPaymentAmount: {
      required: $localize`Payment Amount is required`,
      min: $localize`Payment Amount must be greater than $0.00`,
      max: $localize`Payment Amount cannot be greater than the current account balance`
    }
  };

  public monthDropdowns = Array.from(
    { length: 12 },
    (_, i) => new DropDownItem(moment().month(i).format('MMMM'), i + 1)
  );

  public yearDropdowns = (() => {
    const currentYear = DateUtils.currentYearInCST();
    return [currentYear, currentYear + 1].map(year => new DropDownItem(year.toString(), year));
  })();

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.paymentStepForm && this.paymentStepForm) {
      this.syncDisplayWithFormState();

      this.paymentStepForm.valueChanges.pipe(startWith(this.paymentStepForm.value)).subscribeWhileAlive({
        owner: this,
        next: value => {
          const forgo = value.forgoRecurringPayments;
          const month = value.lumpSumPaymentMonth;
          const year = value.lumpSumPaymentYear;
          const amount = value.lumpSumPaymentAmount;

          if (forgo || month || year || amount) {
            this._displayLumpSumForm.next(true);
          }
        }
      });
    }
  }

  private syncDisplayWithFormState(): void {
    const {
      forgoRecurringPayments,
      lumpSumPaymentAmount: amountControl,
      lumpSumPaymentMonth: monthControl,
      lumpSumPaymentYear: yearControl
    } = this.paymentStepForm.controls;

    const hasValues =
      forgoRecurringPayments.value ||
      amountControl.value !== null ||
      monthControl.value !== null ||
      yearControl.value !== null;

    if (hasValues) {
      this._displayLumpSumForm.next(true);
      return;
    }

    amountControl.disable({ emitEvent: false });
    monthControl.disable({ emitEvent: false });
    yearControl.disable({ emitEvent: false });
    this._displayLumpSumForm.next(false);
  }

  public showLumpSumForm(): void {
    const {
      lumpSumPaymentAmount: amountControl,
      lumpSumPaymentMonth: monthControl,
      lumpSumPaymentYear: yearControl
    } = this.paymentStepForm.controls;

    amountControl.enable();
    monthControl.enable();
    yearControl.enable();
    this._displayLumpSumForm.next(true);
  }

  public deleteLumpSumPaymentInfo(): void {
    this._displayLumpSumForm.next(false);

    const {
      lumpSumPaymentAmount: amountControl,
      lumpSumPaymentMonth: monthControl,
      lumpSumPaymentYear: yearControl
    } = this.paymentStepForm.controls;

    amountControl.reset(null, { emitEvent: false });
    monthControl.reset(null, { emitEvent: false });
    yearControl.reset(null, { emitEvent: false });

    amountControl.disable();
    monthControl.disable();
    yearControl.disable();
    this.lumpSumRemoved.emit();
  }

  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
}
